import { GhgEmissions, GhgEmissionsPropName } from '../../../../models/project/ghg-emission/ghg-emissions.model';
import { ProjectValidationUnit } from '../../../../models/data-structures/project-validation';
import {
  CarbonCredErrorMessage,
  CarbonCreditInputType,
  CarbonCreditPropName,
} from '../../../../models/project/ghg-emission/carbon-credit.model';
import { Emission, EmissionPropName, EmissionRef } from '../../../../models/project/ghg-emission/emission.model';
import { UnitValueValidation } from '../unit-value.validation';
import { TransportationValidation } from './transportation.validation';
import { CarbonCreditPriceValidation } from './carbon-credit-price.validation';
import { ValidationInterface } from '../../validation.interface';
import { ProjectInfo } from '../../../../models/project/project-info.model';
import { UnitValue } from '../../../../models/data-structures/unit-value.model';

export class GhgValidation implements ValidationInterface {
  /** The current GHG emission obj that is being validated */
  ghg: GhgEmissions;
  projectInfo: ProjectInfo;

  /**
   * Sets the GHG emissions obj that will be used for validation
   * @param ghg will be used to set the current GHG emissions obj
   */
  constructor(ghg: GhgEmissions, projectInfo: ProjectInfo) {
    if (!ghg) {
      throw Error('unable to validation ghg emissions');
    }
    this.ghg = new GhgEmissions(ghg);
    this.projectInfo = projectInfo;
  }

  /**
   * Get a validation unit containing the validation data for the current GHG emissions obj
   */
  getValidation() {
    const validationUnit = new ProjectValidationUnit();
    if (!this.ghg) {
     return new ProjectValidationUnit();
    }
    if (this.projectInfo.useGHG) {
      if (this.ghg.includeWasteDisposal) {
        validationUnit.addModuleResults(
          new UnitValueValidation(this.ghg.landfillPercent).getValidation(),
          GhgEmissionsPropName.landfillPercent
        );
        validationUnit.addModuleResults(
          new UnitValueValidation(this.ghg.incinerationPercent).getValidation(),
          GhgEmissionsPropName.incinerationPercent
        );
        validationUnit.addModuleResults(
          new UnitValueValidation(this.ghg.compostingPercent).getValidation(),
          GhgEmissionsPropName.compostingPercent
        );
      }
      if (this.ghg.isTransportation) {
        if (this.ghg.transportations.length === 0) {
          validationUnit.setInvalid(GhgEmissionsPropName.transportations, [GhgErrorMessage.MISSING_TRANSPORTATION]);
          validationUnit.invalidFields += 17;
        } else {
          this.addTransportationValidation(validationUnit);
        }
      }
      this.addEmissionFactorsValidation(validationUnit);
      this.addCarbonCreditValidation(validationUnit);
    } else {
      validationUnit.setValid('VALIDATION.GHG_DISABLED');
    }
    return validationUnit;
  }

  /**
   * returns a boolean that determines if the current GHG emission obj is valid
   */
  isValid(): boolean {
    return this.getValidation().isValid;
  }

  private addTransportationValidation(validationUnit: ProjectValidationUnit) {
    for (let i = 0; i < this.ghg.transportations.length; i++) {
      validationUnit.addArrayToModuleResults(
        new TransportationValidation(this.ghg.transportations[i]).getValidation(),
        GhgEmissionsPropName.transportations,
        i,
        this.ghg.transportations[i].nameTranslation
      );
    }
  }

  /**
   * Adds validation for GHG emission factors
   * @param validationUnit is the validation unit for which the emission factors validation will be added to
   */
  private addEmissionFactorsValidation(validationUnit: ProjectValidationUnit) {
    if (!this.ghg) {
      return;
    }

    const emissionsValidation = new ProjectValidationUnit();
    const emissionKeys = Object.keys(this.ghg).filter(key => key.endsWith('Emission'));

    let isValid = true;
    for (const key of emissionKeys) {
      const ghgKey = key as keyof GhgEmissions;
      if (this.ghg[ghgKey] instanceof EmissionRef) {
       const emissionRef: EmissionRef = this.ghg[ghgKey] as EmissionRef;
        if (emissionRef.isCustom) {
          let index = 0;
          for (const emissionKey of Object.keys(emissionRef.customEmission)) {
            const eKey = emissionKey as keyof Emission;
            if (emissionRef.customEmission[eKey] instanceof UnitValue) {
              const unitValue: UnitValue = emissionRef.customEmission[eKey] as UnitValue;
              if (unitValue.unit || unitValue.value) {
                emissionsValidation.addArrayToModuleResults(
                  new UnitValueValidation(unitValue).getValidation(),
                  GhgEmissionsPropName[ghgKey as keyof typeof GhgEmissionsPropName],
                  index,
                  EmissionPropName[eKey as keyof typeof EmissionPropName],
                );
                index++;
              }
            }
          }
        } else {
          if (emissionRef.databaseEmissionCollection == null || emissionRef.databaseEmissionReference == null) {
            isValid = false;
            break;
          }
        }
      }
    }
    if (Object.keys(emissionsValidation.tree).length > 0) {
      validationUnit.addModuleResults(
        emissionsValidation,
        GhgEmissionsValidationPropName.emissionFactors,
      );
    } else {
      if (isValid) {
        validationUnit.setValid(GhgEmissionsValidationPropName.emissionFactors);
      } else {
        validationUnit.setInvalid(GhgEmissionsValidationPropName.emissionFactors, [GhgErrorMessage.MISSING_EMISSION_FACTORS]);
      }
    }
  }

  /**
   * Adds validation for GHG carbon credit
   * @param validationUnit is the validation unit for which the carbon credit validation will be added to
   */
  private addCarbonCreditValidation(validationUnit: ProjectValidationUnit) {
    if (!this.ghg || !this.ghg.carbonCredit || !this.ghg.carbonCredit.includeCarbonCredit) {
      return;
    }
    const cc = this.ghg.carbonCredit;
    const ccValidation = new ProjectValidationUnit();
    const inputTypeSet = Object.values(CarbonCreditInputType).includes(cc.inputType as CarbonCreditInputType);
    if (!inputTypeSet) {
      ccValidation.setInvalid(CarbonCreditPropName.inputType, [GhgErrorMessage.MISSING_CCP_INPUT_TYPE]);
    } else {
      ccValidation.setValid(CarbonCreditPropName.inputType);
    }
    if (cc.inputType === CarbonCreditInputType.Manual || cc.inputType === CarbonCreditInputType.File) {
      ccValidation.addModuleResults(
        new CarbonCreditPriceValidation(cc.customPrice, cc.inputType).getValidation(),
        CarbonCreditPropName.customPrice
      );
    }
    if (cc.inputType === CarbonCreditInputType.Database) {
      if (!cc.databasePriceCollection || !cc.databasePriceReference) {
        ccValidation.setInvalid(CarbonCreditPropName.databasePriceRef, [CarbonCredErrorMessage.MissingDbRef]);
      } else {
        ccValidation.setValid(CarbonCreditPropName.databasePriceRef);
      }
    }
    if (inputTypeSet) {
      ccValidation.addModuleResults(
        new UnitValueValidation(cc.cpi).getValidation(),
        CarbonCreditPropName.cpi
      );
    }
    validationUnit.addModuleResults(ccValidation, GhgEmissionsPropName.carbonCredit);
  }
}

enum GhgEmissionsValidationPropName {
  emissionFactors = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION_FACTORS',
}

enum GhgErrorMessage {
  MISSING_TRANSPORTATION= 'VALIDATION.MISSING_TRANSPORTATION',
  MISSING_EMISSION_FACTORS = 'VALIDATION.MISSING_EMISSION_FACTORS',
  MISSING_CCP_INPUT_TYPE = 'VALIDATION.MISSING_CCP_INPUT_TYPE',
}
